// Endpoints RTK Query da feature dashboard (visão pessoal do user + chart 7d).
// Tipos manuais espelhando o contrato do back (Onda 3 handoff).
// FIXME: migrar pra paths['/v1/me/metrics']... quando openapi-typescript regenerar.
import { api } from '@/lib/api';
import { qs } from '@/lib/api/qs';

// RF-02: '15d' passa a ser um preset válido do /painel (paridade com /empresas).
export type DashboardPeriod = 'today' | '7d' | '15d' | '30d' | '90d';

export interface MetricsDelta {
  /** Variação absoluta (mesma unidade da métrica). */
  absolute: number | null;
  /** Variação percentual em pontos (-1..+1 ou +Infinity quando base=0). */
  pct: number | null;
}

/** RF-24: top vendedor no payload agregado do gestor. */
export interface MeTopVendor {
  userId: string;
  name: string;
  avatarTone: number;
  storeId: string | null;
  vendasCount: number;
  /** Valor em REAIS. */
  vendasBRL: number;
}

export interface MeMetricsDto {
  /** ResolvedPeriod — pode ser 'custom' quando o back recebeu from/to. */
  period: DashboardPeriod | 'custom';
  /** ISO inclusive. */
  from: string;
  /** ISO exclusive. */
  to: string;
  /**
   * RF-24: `me` = métricas individuais (VENDEDOR/SUPERVISOR); `store` = agregado
   * da loja do GESTOR. O front usa isto para o layout (esconder "Atendimentos no
   * período" e mostrar Top 3 quando `store`).
   */
  scope: 'me' | 'store';
  /** `null` no agregado do gestor (`scope='store'`) — não somar atendimentos individuais. */
  atendimentosHoje: number | null;
  /** Nº de vendas concluídas (conclusionReason='SALE') no escopo. */
  vendasCount: number;
  /** Soma de saleValueCents/100 (REAIS) das vendas no escopo. */
  vendasBRL: number;
  concluidos: number;
  ativos: number;
  /** CSAT real do cliente (1–5) dos atendimentos que o user concluiu.
   *  0 = sem respostas no período — renderizar "—", nunca nota zero. */
  csat: number;
  /** Nº de pesquisas de satisfação respondidas no período. */
  csatRespostas: number;
  /** Respondidas / enviadas (0..1). */
  csatTaxaResposta: number;
  tempoMedioMs: number;
  /** Top 3 vendedores por vendasBRL — presente só no agregado do gestor. */
  top3?: MeTopVendor[];
  deltas: {
    atendimentosHoje: MetricsDelta;
    vendasBRL: MetricsDelta;
    csat: MetricsDelta;
  };
}

export interface VolumeChartPoint extends Record<string, unknown> {
  /** "Seg", "Ter"... ou ISO date depending no back. */
  day: string;
  ia: number;
  humano: number;
}

export interface VolumeChartDto {
  /** Pode vir 'custom' quando o back recebeu from/to (RF-02). */
  period: DashboardPeriod | 'custom';
  data: VolumeChartPoint[];
}

/** Ponto esparso de atendimentos por hora (UTC). Só horas com count>0 vêm do back. */
export interface RealtimePorHora {
  /** Hora 0..23 em UTC. */
  hour: number;
  count: number;
}

export interface RealtimeCountsDto {
  /** Conversas em fila (status QUEUE). */
  aguardando: number;
  /** Conversas ativas com humano (status ACTIVE). */
  emAtendimento: number;
  /** Conversas sendo tocadas pela IA (status IA). */
  comIA: number;
  /** Concluídas hoje (status DONE). */
  concluidosHoje: number;
  /** Atendimentos de hoje por hora, ESPARSO (só horas com count>0), hour em UTC. */
  porHora: RealtimePorHora[];
}

export interface RealtimeCountsArgs {
  storeId?: string;
}

export interface MeMetricsArgs {
  period: DashboardPeriod;
  /** RF-02: intervalo custom (date-only YYYY-MM-DD). Quando os dois vêm juntos o
   *  back resolve `period:'custom'`. */
  from?: string;
  to?: string;
  /** RF-24: gestor passa a loja do filtro → back devolve o agregado (scope='store'). */
  storeId?: string;
}

export interface VolumeChartArgs {
  period: DashboardPeriod;
  from?: string;
  to?: string;
  storeId?: string;
}

export const dashboardApi = api.injectEndpoints({
  endpoints: (build) => ({
    meMetrics: build.query<MeMetricsDto, MeMetricsArgs>({
      query: ({ period, from, to, storeId }) => `/me/metrics${qs({ period, from, to, storeId })}`,
      providesTags: ['Metrics'],
    }),
    chartVolume: build.query<VolumeChartDto, VolumeChartArgs>({
      query: ({ period, from, to, storeId }) => `/charts/volume${qs({ period, from, to, storeId })}`,
    }),
    realtimeCounts: build.query<RealtimeCountsDto, RealtimeCountsArgs | void>({
      query: (args) => `/realtime/counts${qs({ storeId: args?.storeId })}`,
      providesTags: ['RealtimeCounts'],
    }),
  }),
  overrideExisting: false,
});

export const { useMeMetricsQuery, useChartVolumeQuery, useRealtimeCountsQuery } = dashboardApi;
